import { z, type ZodType } from "zod";
import { rememberResponseTime } from "./response-time";
import { abortable, abortableDelay } from "./abortable";
import { consumeSse, type SseEvent } from "./sse";
import { ELYSIUM } from "../config/elysium";

const emptyResponseSchema = z.undefined();

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly retryAfterMs: number | null,
    public readonly details: unknown,
  ) {
    super(message);
  }
}

/** The request belongs to an obsolete context; never replay a write automatically. */
export class ApiSessionChangedError extends Error {
  constructor() {
    super("API session changed while waiting to read");
    this.name = "ApiSessionChangedError";
  }
}

/** Transport observation is independent of a cancelled/expired request waiter. */
export type PostTransportObserver = (completion: Promise<{ status: number; retryAfterMs: number | null } | null>) => void;

export type ApiClientOptions = Readonly<{
  baseUrl?: string;
  getSessionRevision?: () => number;
  getCsrfToken?: () => string | null;
  canRequestProtected?: () => boolean;
  onCsrfFailure?: (code: string) => void;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
  /** Includes response-body consumption; cooldown waiting has its own cancellation. */
  timeoutMs?: number | Readonly<{ get?: number; post?: number }>;
}>;

type Cooldown = { failures: number; until: number };
const RETRYABLE_STATUSES = [429, 502, 503, 504];

export class ApiClient {
  private readonly cookieRequests = new Set<Promise<Response>>();
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeouts: Readonly<{ GET: number; POST: number }>;
  private cooldownScope: { session: number | undefined; entries: Map<string, Cooldown> } = {
    session: undefined, entries: new Map(),
  };
  private readonly publicCooldownScope = { session: undefined, entries: new Map<string, Cooldown>() };

  constructor(private readonly options: ApiClientOptions = {}, private readonly cookieLockHeld = false) {
    this.baseUrl = (options.baseUrl ?? "/api").replace(/\/$/, "");
    // Some EIP-1193 browser shells expose Window.fetch as a Web-IDL method
    // that rejects an unbound invocation. Preserve the Window receiver while
    // keeping injected test clients untouched.
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    const timeout = options.timeoutMs;
    this.timeouts = { GET: typeof timeout === "number" ? timeout : timeout?.get ?? 45_000,
      POST: typeof timeout === "number" ? timeout : timeout?.post ?? 60_000 };
    if (Object.values(this.timeouts).some((value) => !Number.isFinite(value) || value <= 0 || value > 2_147_483_647)) {
      throw new RangeError("API timeouts must be positive finite browser timer durations");
    }
  }

  get<T>(path: string, schema: ZodType<T>, signal?: AbortSignal): Promise<T> {
    return this.request("GET", path, schema, undefined, signal);
  }

  post<T>(path: string, body: unknown, schema: ZodType<T>, signal?: AbortSignal, observeTransport?: PostTransportObserver): Promise<T> {
    return this.request("POST", path, schema, body, signal, observeTransport);
  }

  /** Fetch-based SSE preserves the configured API origin, cookie auth and test transport. */
  async stream(path: string, options: {
    signal: AbortSignal;
    lastEventId?: string;
    onEvent(event: SseEvent): void;
    onRetry(milliseconds: number): void;
  }): Promise<void> {
    options.signal.throwIfAborted();
    const policy = endpointPolicy("GET", path);
    const publicRead = policy === "public";
    if (!publicRead) this.assertProtected();
    const session = publicRead ? undefined : this.options.getSessionRevision?.();
    const abort = new AbortController();
    const forwardAbort = () => abort.abort(options.signal.reason);
    options.signal.addEventListener("abort", forwardAbort, { once: true });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const armDeadline = () => {
      clearTimeout(timer);
      timer = setTimeout(() => abort.abort(new ApiError(504, "STREAM_IDLE_TIMEOUT",
        "Live updates stopped responding", null, null)), this.timeouts.GET);
    };
    const checkSession = () => {
      if (!publicRead && session !== this.options.getSessionRevision?.()) throw new ApiSessionChangedError();
      abort.signal.throwIfAborted();
    };
    let handledFailure = false;
    armDeadline();
    try {
      await abortable(async () => {
        const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
          method: "GET", signal: abort.signal,
          credentials: publicRead ? "omit" : "include",
          headers: { Accept: "text/event-stream",
            ...(options.lastEventId ? { "Last-Event-ID": options.lastEventId } : {}) },
        });
        try { checkSession(); }
        catch (error) { response.body?.cancel().catch(() => {}); throw error; }
        if (!response.ok) {
          const payload = await response.json().catch(() => null) as unknown;
          checkSession();
          const record = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
          handledFailure = true;
          if (!publicRead && response.status === 401 && record.error !== "X_REAUTH_REQUIRED") this.options.onUnauthorized?.();
          if (!publicRead && response.status === 403 && typeof record.error === "string" && record.error.startsWith("CSRF_")) this.options.onCsrfFailure?.(record.error);
          throw new ApiError(response.status, typeof record.error === "string" ? record.error : "STREAM_FAILED",
            typeof record.message === "string" ? record.message : `Stream failed (${response.status})`,
            retryAfter(response.headers.get("Retry-After")), record.details ?? null);
        }
        if (response.headers.get("Content-Type")?.split(";")[0]?.trim().toLowerCase() !== "text/event-stream" || !response.body) {
          if (response.body) response.body.cancel().catch(() => {});
          throw new ApiError(502, "INVALID_STREAM_RESPONSE", "Server did not return an event stream", null, null);
        }
        armDeadline();
        await consumeSse(response.body, abort.signal, (event) => {
          checkSession(); options.onEvent(event);
        }, () => { checkSession(); armDeadline(); }, milliseconds => { checkSession(); options.onRetry(milliseconds); });
        checkSession();
      }, abort.signal);
    } catch (error) {
      if (!handledFailure) checkSession();
      throw error;
    } finally {
      clearTimeout(timer);
      options.signal.removeEventListener("abort", forwardAbort);
      abort.abort();
    }
  }

  assertCookieCoordination(): void {
    if (typeof navigator === "undefined" || !navigator.locks) {
      throw new ApiError(0, "COOKIE_AUTH_COORDINATION_UNAVAILABLE",
        "Cookie sign-in/logout requires a secure browser context with Web Locks. Check browser configuration and retry.", null, null);
    }
  }

  /** Order the whole verify/confirm or inspect/logout transaction across tabs. */
  async cookieTransaction<T>(work: (api: ApiClient) => Promise<T>): Promise<T> {
    this.assertCookieCoordination();
    return cookieAuthRequest(this.baseUrl, async () => {
      const api = new ApiClient({ ...this.options, fetchImpl: this.fetchImpl }, true);
      try { return await work(api); }
      finally {
        // A timeout/cancelled JS waiter does not release the cookie mutation lock
        // until all actual fetches settle and can no longer apply Set-Cookie.
        await Promise.allSettled([...api.cookieRequests]);
      }
    });
  }
  private assertProtected(): void {
    if (this.options.canRequestProtected?.() === false) throw new ApiSessionChangedError();
  }

  /** Only logout admits an empty response; JSON endpoints keep their schemas. */
  logout(signal?: AbortSignal): Promise<void> {
    return this.post("/auth/logout", undefined, emptyResponseSchema, signal);
  }

  private async request<T>(method: "GET" | "POST", path: string, schema: ZodType<T>, body?: unknown,
    signal?: AbortSignal, observeTransport?: PostTransportObserver): Promise<T> {
    signal?.throwIfAborted();
    const policy = method === "GET" && /^\/tokens\/0x[0-9a-fA-F]{40}$/.test(path)
      ? "public" : endpointPolicy(method, path);
    const publicRead = policy !== "protected";
    const session = publicRead ? undefined : this.options.getSessionRevision?.();
    if (!publicRead) this.assertProtected();
    const csrf = method === "POST" && (policy === "protected" || policy === "logout") ? this.options.getCsrfToken?.() : null;
    if (method === "POST" && (policy === "protected" || policy === "logout") && !csrf) {
      throw new ApiError(403, "CSRF_TOKEN_UNAVAILABLE", "Check the cookie session before trying this action again", null, null);
    }
    const checkSession = () => {
      if (!publicRead && session !== this.options.getSessionRevision?.()) throw new ApiSessionChangedError();
    };
    if (!publicRead && this.cooldownScope.session !== session) this.cooldownScope = { session, entries: new Map() };
    const scope = publicRead ? this.publicCooldownScope : this.cooldownScope;
    if (method === "GET") {
      // Do not replay the failed request. Only a later caller waits for recovery.
      let wait = (scope.entries.get(path)?.until ?? 0) - performance.now();
      while (wait > 0) {
        await abortableDelay(wait, signal);
        checkSession();
        wait = (scope.entries.get(path)?.until ?? 0) - performance.now();
      }
    }
    signal?.throwIfAborted();
    checkSession();
    const cooldownAtDispatch = scope.entries.get(path);
    const abort = new AbortController();
    const forwardAbort = () => abort.abort(signal?.reason);
    signal?.addEventListener("abort", forwardAbort, { once: true });
    const timer = setTimeout(() => abort.abort(new ApiError(504, "REQUEST_TIMEOUT",
      "The API request timed out. Try again; a submitted action may still have reached the server.", null, null)),
    this.timeouts[method]);
    const startedAt = performance.now();
    let handledFailure = false;
    try {
      const result = await abortable(async () => {
        const init: RequestInit = {
          method,
          signal: abort.signal,
          credentials: policy === "public" || policy === "nonce" ? "omit" : "include",
          ...(policy === "public" ? {} : { cache: "no-store" as const }),
          headers: {
            Accept: "application/json",
            ...(body === undefined ? {} : { "Content-Type": "application/json" }),
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        };
        const dispatch = () => {
          abort.signal.throwIfAborted(); checkSession();
          if (!publicRead) this.assertProtected();
          const request = this.fetchImpl(`${this.baseUrl}${path}`, init);
          // Observe the raw fetch even if abortable() has already released its waiter.
          // A rejected fetch is an unknown server outcome, not proof of rollback.
          observeTransport?.(request.then(response => ({ status: response.status,
            retryAfterMs: retryAfter(response.headers.get("Retry-After")) }), () => null));
          if (this.cookieLockHeld) this.cookieRequests.add(request);
          return request;
        };
        const response = await ((policy === "cookie-auth" || policy === "logout") && !this.cookieLockHeld ? cookieAuthRequest(this.baseUrl, dispatch) : dispatch());
        abort.signal.throwIfAborted();
        checkSession();
        const payload = response.status === 204 ? undefined : await response.json().catch(() => null) as unknown;
        abort.signal.throwIfAborted();
        checkSession();
        if (!response.ok) {
          const record = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
          handledFailure = true;
          // X reauthentication is separate from the SIWE wallet session. Other
          // 401s, including those from X endpoints, retain session expiry handling.
          if (response.status === 401 && record.error !== "X_REAUTH_REQUIRED" &&
            !publicRead && session === this.options.getSessionRevision?.()) {
            this.options.onUnauthorized?.();
          }
          if (!publicRead && response.status === 403 && typeof record.error === "string" && record.error.startsWith("CSRF_")) {
            this.options.onCsrfFailure?.(record.error);
          }
          rememberResponseTime(record.details, response.headers, startedAt);
          throw new ApiError(
            response.status,
            typeof record.error === "string" ? record.error : "REQUEST_FAILED",
            typeof record.message === "string" ? record.message : `Request failed (${response.status})`,
            retryAfter(response.headers.get("Retry-After")),
            record.details ?? null,
          );
        }
        if (response.status === 204 && policy !== "logout") throw new ApiError(502, "INVALID_SERVER_RESPONSE", "Expected a JSON response", null, null);
        const parsed = schema.safeParse(payload);
        if (!parsed.success) throw new ApiError(502, "INVALID_SERVER_RESPONSE", "Server returned an invalid response", null,
          parsed.error.flatten());
        rememberResponseTime(parsed.data, response.headers, startedAt);
        return parsed.data;
      }, abort.signal);
      // A concurrent failure may have introduced a newer Retry-After while this
      // request was in flight. An earlier successful request cannot clear it.
      if (method === "GET" && (publicRead || scope === this.cooldownScope) && scope.entries.get(path) === cooldownAtDispatch) {
        scope.entries.delete(path);
      }
      return result;
    } catch (error) {
      if (!handledFailure) checkSession();
      if (method === "GET" && !signal?.aborted && (publicRead || scope === this.cooldownScope) &&
        (publicRead || session === this.options.getSessionRevision?.()) &&
        (error instanceof TypeError || error instanceof ApiError && RETRYABLE_STATUSES.includes(error.status))) {
        const failures = Math.min((scope.entries.get(path)?.failures ?? 0) + 1, 6);
        const backoff = Math.min(1_000 * 2 ** (failures - 1), 30_000) * (1 + Math.random() * 0.2);
        const delay = Math.max(backoff, error instanceof ApiError ? error.retryAfterMs ?? 0 : 0);
        scope.entries.set(path, { failures,
          until: Math.max(scope.entries.get(path)?.until ?? 0, performance.now() + delay) });
      }
      throw error;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", forwardAbort);
    }
  }
}

function publicSnapshotPath(path: string): boolean {
  const route = path.split("?")[0];
  if (route === "/nfts/history") return true;
  if (route === "/markets/tiptoad-native/market-cap/ticks" || route === "/markets/tiptoad-native/market-cap/stream") return true;
  if (route && /^\/tokens\/0x[0-9a-f]{40}\/swaps(?:\/stream)?$/i.test(route)) return true;
  // The V3 public contract is known only for this admitted source. Retained
  // legacy contexts keep their original authentication/cooldown semantics.
  return path.toLowerCase().startsWith(`/tokens/${ELYSIUM.token.toLowerCase()}/`) &&
    /^\/tokens\/0x[0-9a-f]{40}\/(?:wallets\/0x[0-9a-f]{40}\/net-buy|reward-pool)(?:\/stream)?$/i.test(path);
}

function retryAfter(value: string | null): number | null {
  if (!value) return null;
  if (/^[0-9]+$/.test(value)) {
    const milliseconds = Number(value) * 1_000;
    return Number.isFinite(milliseconds) ? milliseconds : null;
  }
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : null;
}

export async function withRetryAfter<T>(operation: () => Promise<T>, options: {
  attempts?: number;
  signal?: AbortSignal;
  baseDelayMs?: number;
} = {}): Promise<T> {
  const attempts = options.attempts ?? 3;
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    options.signal?.throwIfAborted();
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (!(error instanceof ApiError) || !RETRYABLE_STATUSES.includes(error.status) || attempt + 1 >= attempts) throw error;
      const delay = error.retryAfterMs ?? (options.baseDelayMs ?? 400) * 2 ** attempt;
      await abortableDelay(delay, options.signal);
    }
  }
  throw lastError;
}

/** Explicit transport policy. Unknown routes fail into cookie protection. */
function endpointPolicy(method: "GET" | "POST", path: string): "public" | "nonce" | "cookie-auth" | "logout" | "protected" {
  const route = path.split("?")[0];
  if (method === "POST" && route === "/auth/nonce") return "nonce";
  if (method === "POST" && route === "/auth/verify-cookie" || method === "GET" && route === "/auth/session") return "cookie-auth";
  if (method === "POST" && route === "/auth/logout") return "logout";
  if (method === "GET" && (publicSnapshotPath(path) || route === "/app/bootstrap" || route === "/mint/fee" ||
    route === "/rewards/policy" || route === "/health" || route === "/health/randomness" ||
    route === "/mining/difficulty")) return "public";
  return "protected";
}

// Serialize cookie reads/mutations, even if a caller stops awaiting a response.
// Web Locks also order cooperating tabs. Keep the lock until fetch settles:
// ignoring stale JS cannot undo a Set-Cookie already applied by the browser.
const cookieQueues = new Map<string, Promise<unknown>>();
function cookieAuthRequest<T>(scope: string, work: () => Promise<T>): Promise<T> {
  if (typeof location !== "undefined") scope = new URL(scope, location.href).origin;
  const lockName = `merklewood.cookie-auth:${scope}`;
  const run = async (): Promise<T> => typeof navigator !== "undefined" && navigator.locks
    ? await navigator.locks.request(lockName, work) : await work();
  const pending = (cookieQueues.get(scope) ?? Promise.resolve()).then(run, run);
  const settled = pending.catch(() => undefined);
  cookieQueues.set(scope, settled);
  const release = () => { if (cookieQueues.get(scope) === settled) cookieQueues.delete(scope); };
  settled.then(release, release);
  return pending;
}
